import { AtomicStep, AuditInstance, SEOModule, StepAnswer } from '../types';

export interface ValidationError {
  fieldId: string;
  message: string;
}

export class EngineService {
  /**
   * Migrate and upgrade existing audit instances to include any newly added step fields and structured choices
   */
  static migrateAudit(audit: AuditInstance, module: SEOModule): AuditInstance {
    const updatedAnswers = { ...audit.answers };

    for (const section of module.sections) {
      for (const step of section.steps) {
        const existing = updatedAnswers[step.id];
        if (existing) {
          const newFieldValues = { ...existing.fieldValues };

          // Backward compatibility mapping for Step 13 (Keyword Focus)
          if (step.id === 'step-keyword-focus') {
            if (!newFieldValues.observedKeywordPattern) {
              if (newFieldValues.hasClearFocus === 'yes') {
                newFieldValues.observedKeywordPattern = 'clear_target';
              } else if (newFieldValues.hasClearFocus === 'no') {
                newFieldValues.observedKeywordPattern = 'unclear_target';
              } else {
                newFieldValues.observedKeywordPattern = 'clear_target';
              }
            }
            if (newFieldValues.focusExplanation && !newFieldValues.additionalComments) {
              newFieldValues.additionalComments = newFieldValues.focusExplanation;
            }
          }

          // Backward compatibility mapping for Step 15 (Content Growth)
          if (step.id === 'step-static-vs-dynamic') {
            if (!newFieldValues.contentGrowthClassification) {
              if (newFieldValues.siteContentType === 'dynamic') {
                newFieldValues.contentGrowthClassification = 'dynamic_regular';
              } else if (newFieldValues.siteContentType === 'static') {
                newFieldValues.contentGrowthClassification = 'static_stalled';
              } else {
                newFieldValues.contentGrowthClassification = 'dynamic_regular';
              }
            }
            if (newFieldValues.contentGrowthNotes && !newFieldValues.additionalComments) {
              newFieldValues.additionalComments = newFieldValues.contentGrowthNotes;
            }
          }

          // Backward compatibility mapping for Step 23 (Google Indexation)
          if (step.id === 'step-indexation-google') {
            if (!newFieldValues.indexationClassification) {
              newFieldValues.indexationClassification = 'normal_indexation';
            }
            if (newFieldValues.indexationNotes && !newFieldValues.additionalComments) {
              newFieldValues.additionalComments = newFieldValues.indexationNotes;
            }
          }

          // Backward compatibility mapping for Step 26 (Siteliner)
          if (step.id === 'step-siteliner') {
            if (!newFieldValues.sitelinerAccessStatus) {
              newFieldValues.sitelinerAccessStatus = 'accessible';
            }
          }

          // Backward compatibility mapping for Step 30 (Social Media)
          if (step.id === 'step-social-media') {
            if (!newFieldValues.socialMediaClassification) {
              newFieldValues.socialMediaClassification =
                newFieldValues.hasSocialPresence === 'yes' ? 'active_linked' : 'no_social_presence';
            }
          }

          // Ensure all step fields have initialized values if missing
          for (const field of step.fields) {
            if (newFieldValues[field.id] === undefined) {
              newFieldValues[field.id] = field.defaultValue ?? '';
            }
          }

          updatedAnswers[step.id] = {
            ...existing,
            fieldValues: newFieldValues,
          };
        }
      }
    }

    return {
      ...audit,
      methodologyVersion: 'zynex-v1.0',
      answers: updatedAnswers,
    };
  }

  /**
   * Check if a step is unlocked based on its dependencies
   */
  static isStepUnlocked(step: AtomicStep, audit: AuditInstance, _module?: SEOModule): boolean {
    if (!step.dependencies || step.dependencies.length === 0) {
      return true;
    }

    return step.dependencies.every(depId => {
      const depAnswer = audit.answers[depId];
      return depAnswer && depAnswer.completed;
    });
  }

  /**
   * Validate form fields for a given step
   */
  static validateStepFields(
    step: AtomicStep,
    fieldValues: Record<string, any>,
    audit: AuditInstance
  ): ValidationError[] {
    const errors: ValidationError[] = [];

    // Special bypass logic for Siteliner step
    const isSitelinerBypassed =
      step.id === 'step-siteliner' &&
      (fieldValues.sitelinerAccessStatus === 'blocked_bypass' ||
        fieldValues.sitelinerAccessStatus === 'unreachable_bypass');

    for (const field of step.fields) {
      const val = fieldValues[field.id];

      // If Siteliner is bypassed, skip requirement checks on duplicate percentage fields
      if (
        isSitelinerBypassed &&
        (field.id === 'duplicateContentPct' ||
          field.id === 'commonContentPct' ||
          field.id === 'uniqueContentPct')
      ) {
        continue;
      }

      // Required check
      if (field.required) {
        let isEmpty = false;
        if (field.type === 'multiselect') {
          if (Array.isArray(val)) {
            isEmpty = val.length === 0;
          } else {
            isEmpty = !val || String(val).trim().length === 0;
          }
        } else {
          isEmpty = val === undefined || val === null || val === '';
        }

        if (isEmpty) {
          errors.push({
            fieldId: field.id,
            message: `"${field.label}" is required. Select at least one option.`,
          });
          continue;
        }
      }

      if (val === undefined || val === null || val === '') {
        continue;
      }

      // Integer check
      if (field.type === 'integer') {
        const num = Number(val);
        if (isNaN(num) || !Number.isInteger(num)) {
          errors.push({
            fieldId: field.id,
            message: `"${field.label}" must be a valid whole number.`,
          });
        } else {
          if (field.min !== undefined && num < field.min) {
            errors.push({
              fieldId: field.id,
              message: `"${field.label}" cannot be less than ${field.min}.`,
            });
          }
          if (field.max !== undefined && num > field.max) {
            errors.push({
              fieldId: field.id,
              message: `"${field.label}" cannot exceed ${field.max}.`,
            });
          }
        }
      }

      // Decimal check
      if (field.type === 'decimal') {
        const num = Number(val);
        if (isNaN(num)) {
          errors.push({
            fieldId: field.id,
            message: `"${field.label}" must be a valid number.`,
          });
        } else {
          if (field.min !== undefined && num < field.min) {
            errors.push({
              fieldId: field.id,
              message: `"${field.label}" cannot be less than ${field.min}.`,
            });
          }
          if (field.max !== undefined && num > field.max) {
            errors.push({
              fieldId: field.id,
              message: `"${field.label}" cannot exceed ${field.max}.`,
            });
          }
        }
      }

      // URL check
      if (field.type === 'url') {
        try {
          new URL(String(val));
        } catch {
          errors.push({
            fieldId: field.id,
            message: `"${field.label}" must be a valid URL (e.g. https://example.com).`,
          });
        }
      }

      // Cross-field reference check
      if (field.maxRefFieldId) {
        let maxRefVal: number | undefined;

        if (fieldValues[field.maxRefFieldId] !== undefined) {
          maxRefVal = Number(fieldValues[field.maxRefFieldId]);
        } else {
          for (const ans of Object.values(audit.answers)) {
            if (ans.fieldValues[field.maxRefFieldId] !== undefined) {
              maxRefVal = Number(ans.fieldValues[field.maxRefFieldId]);
              break;
            }
          }
        }

        if (maxRefVal !== undefined && !isNaN(maxRefVal)) {
          const num = Number(val);
          if (num > maxRefVal) {
            errors.push({
              fieldId: field.id,
              message: `"${field.label}" (${num}) cannot be greater than total count (${maxRefVal}).`,
            });
          }
        }
      }
    }

    return errors;
  }

  /**
   * Run calculations for step
   */
  static runCalculations(
    step: AtomicStep,
    fieldValues: Record<string, any>,
    audit: AuditInstance
  ): Record<string, any> {
    const calculated: Record<string, any> = {};

    if (!step.calculations) return calculated;

    for (const calc of step.calculations) {
      if (calc.formula === 'percentage' && calc.affectedFieldId && calc.totalFieldId) {
        let affectedVal: number | undefined;
        let totalVal: number | undefined;

        if (fieldValues[calc.affectedFieldId] !== undefined) {
          affectedVal = Number(fieldValues[calc.affectedFieldId]);
        }

        if (fieldValues[calc.totalFieldId] !== undefined) {
          totalVal = Number(fieldValues[calc.totalFieldId]);
        } else {
          for (const ans of Object.values(audit.answers)) {
            if (ans.fieldValues[calc.totalFieldId] !== undefined) {
              totalVal = Number(ans.fieldValues[calc.totalFieldId]);
              break;
            }
          }
        }

        if (
          affectedVal !== undefined &&
          totalVal !== undefined &&
          !isNaN(affectedVal) &&
          !isNaN(totalVal) &&
          totalVal > 0
        ) {
          const pct = Math.round((affectedVal / totalVal) * 100 * 10) / 10;
          calculated[calc.id] = pct;
          calculated[`${calc.id}_display`] = `${affectedVal} / ${totalVal} (${pct}%)`;
        }
      }
    }

    return calculated;
  }

  /**
   * Check if step criteria are fulfilled for automatic completion
   */
  static isStepComplete(
    step: AtomicStep,
    fieldValues: Record<string, any>,
    errors: ValidationError[],
    answer?: StepAnswer
  ): boolean {
    if (errors.length > 0) return false;

    const isSitelinerBypassed =
      step.id === 'step-siteliner' &&
      (fieldValues.sitelinerAccessStatus === 'blocked_bypass' ||
        fieldValues.sitelinerAccessStatus === 'unreachable_bypass');

    for (const field of step.fields) {
      if (
        isSitelinerBypassed &&
        (field.id === 'duplicateContentPct' ||
          field.id === 'commonContentPct' ||
          field.id === 'uniqueContentPct')
      ) {
        continue;
      }

      if (field.required) {
        const val = fieldValues[field.id];
        let isEmpty = false;
        if (field.type === 'multiselect') {
          if (Array.isArray(val)) {
            isEmpty = val.length === 0;
          } else {
            isEmpty = !val || String(val).trim().length === 0;
          }
        } else {
          isEmpty = val === undefined || val === null || val === '';
        }
        if (isEmpty) return false;
      }
    }

    if (step.requiresEvidence && !isSitelinerBypassed) {
      if (!answer || !answer.evidence || !answer.evidence.dataUrl) {
        return false;
      }
    }

    return true;
  }

  /**
   * Recalculate dependent answers when a prior step value changes
   */
  static recalculateAllDependentAnswers(audit: AuditInstance, module: SEOModule): AuditInstance {
    const updatedAnswers = { ...audit.answers };

    for (const section of module.sections) {
      for (const step of section.steps) {
        const answer = updatedAnswers[step.id];
        if (answer) {
          const newCalcs = this.runCalculations(step, answer.fieldValues, audit);
          updatedAnswers[step.id] = {
            ...answer,
            calculatedValues: newCalcs,
          };
        }
      }
    }

    return {
      ...audit,
      answers: updatedAnswers,
    };
  }
}
